import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Users, Wallet, AlertCircle, TrendingUp, MessageSquare, Clock, ArrowUpRight, RefreshCw, Smartphone, ArrowUp, ArrowDown, FileCheck2, PiggyBank, Receipt, CalendarClock, CalendarRange } from "lucide-react";

// Intervalo de auto-refresh en ms. El dashboard consulta al backend
// cada este tiempo para reflejar anulaciones de pagos, nuevas validaciones,
// etc. sin que el usuario tenga que recargar la página.
const AUTO_REFRESH_MS = 30_000;

const KPI = ({ icon: Icon, label, value, prefix = "", suffix = "", accent = "from-green-700 to-green-500", testid, hint = null }) => (
  <Card className="kpi-card p-5 border-green-100 bg-white" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[11px] uppercase tracking-wider font-semibold text-green-700/60">{label}</p>
        <p className="font-display font-extrabold text-3xl text-green-900 mt-2">
          {prefix}{value}{suffix}
        </p>
        {hint && <div className="mt-1.5">{hint}</div>}
      </div>
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-md`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </Card>
);

function formatUltimaActualizacion(date) {
  if (!date) return "—";
  const diff = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diff < 5) return "hace instantes";
  if (diff < 60) return `hace ${diff} s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  return date.toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit", timeZone: "America/Caracas" });
}

export default function DashboardHome() {
  const { token, API, user } = useAuth();
  const [data, setData] = useState(null);
  const [cotizacion, setCotizacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tick, setTick] = useState(0); // fuerza recálculo del "hace X seg"
  const mountedRef = useRef(true);

  const fetchResumen = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [r, rc] = await Promise.all([
        axios.get(`${API}/dashboard/resumen`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/definiciones/cotizaciones-moneda/activa`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
      ]);
      if (!mountedRef.current) return;
      setData(r.data);
      setCotizacion(rc?.data && Object.keys(rc.data).length ? rc.data : null);
      setLastUpdated(new Date());
    } catch {
      // silent fail; el UI mantiene los datos previos
    } finally {
      if (!mountedRef.current) return;
      setLoading(false);
      setRefreshing(false);
    }
  }, [API, token]);

  // Fetch inicial
  useEffect(() => {
    mountedRef.current = true;
    fetchResumen();
    return () => { mountedRef.current = false; };
  }, [fetchResumen]);

  // Auto-refresh cada 30 s (silencioso, sin bloquear la UI)
  useEffect(() => {
    const id = setInterval(() => fetchResumen({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchResumen]);

  // Refrescar cuando el usuario vuelve a la pestaña (útil si anuló un pago en otra ruta/tab)
  useEffect(() => {
    const onFocus = () => fetchResumen({ silent: true });
    const onVisibility = () => { if (!document.hidden) fetchResumen({ silent: true }); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchResumen]);

  // Escuchar evento global lanzado por otras páginas (ej. anular pago) para refresco inmediato
  useEffect(() => {
    const onDataChanged = () => fetchResumen({ silent: true });
    window.addEventListener("sisaur:data-changed", onDataChanged);
    return () => window.removeEventListener("sisaur:data-changed", onDataChanged);
  }, [fetchResumen]);

  // Tick de reloj para refrescar la etiqueta "hace X seg"
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  if (loading && !data) return <div className="text-green-700" data-testid="dashboard-loading">Cargando dashboard…</div>;
  if (!data) return <div className="text-red-600">No fue posible cargar el resumen.</div>;

  const fmtBs = (n) => `Bs. ${(Number(n) || 0).toLocaleString("es-VE", { maximumFractionDigits: 2 })}`;

  // Equivalentes en USD/EUR según la Cotización de Moneda con estatus Activo.
  // `cotizacion.dolar` y `cotizacion.euro` representan Bs por 1 USD / 1 EUR.
  const usdRate = Number(cotizacion?.dolar) || 0;
  const eurRate = Number(cotizacion?.euro) || 0;
  const toUSD = (bs) => (usdRate > 0 ? (Number(bs) || 0) / usdRate : null);
  const toEUR = (bs) => (eurRate > 0 ? (Number(bs) || 0) / eurRate : null);
  const fmtUSD = (n) => (n == null ? "—" : `USD ${n.toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`);
  const fmtEUR = (n) => (n == null ? "—" : `EUR ${n.toLocaleString("de-DE", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`);

  const fxHint = (bs, contribCount = null) => {
    const contribLine = contribCount != null ? (
      <p className="text-[10.5px] text-slate-600 mb-0.5" data-testid="kpi-contribs-count">
        <Users className="w-3 h-3 inline-block mr-1 -mt-0.5 text-slate-500" />
        <span className="font-semibold text-slate-800">{contribCount}</span>
        <span className="text-slate-500"> {contribCount === 1 ? "contribuyente" : "contribuyentes"}</span>
      </p>
    ) : null;
    if (!cotizacion || (!usdRate && !eurRate)) {
      return (
        <div className="leading-snug space-y-0.5">
          {contribLine}
          <p className="text-[10px] text-slate-400" data-testid="kpi-fx-missing">Sin cotización activa</p>
        </div>
      );
    }
    return (
      <div className="text-[10.5px] text-slate-500 leading-snug space-y-0.5" data-testid="kpi-fx-hint">
        {contribLine}
        <p><span className="font-semibold text-green-800">≈ {fmtUSD(toUSD(bs))}</span></p>
        <p><span className="font-semibold text-orange-700">≈ {fmtEUR(toEUR(bs))}</span></p>
      </div>
    );
  };

  const serie = (data.serie_7dias || []).map(d => ({
    label: d.fecha.slice(5),
    monto: d.monto,
  }));

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-green-900">¡Hola, {user?.nombre}!</h1>
          <p className="text-green-700/70 mt-1">Resumen ejecutivo de SISAUR · Palma Plus</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-green-700/70" data-testid="dashboard-refresh-bar">
          <span className="hidden sm:inline">Última actualización: <b data-testid="dashboard-last-updated" title={lastUpdated?.toLocaleString("es-VE", { timeZone: "America/Caracas" }) || ""}>{/* tick fuerza recalculo */}{tick >= 0 ? formatUltimaActualizacion(lastUpdated) : ""}</b> <span className="text-slate-400 text-[10px]">VET</span></span>
          <span className="text-green-700/40 hidden sm:inline">·</span>
          <span className="hidden sm:inline">Auto-refresh 30 s</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchResumen({ silent: true })}
            disabled={refreshing}
            className="border-green-200 text-green-800 hover:bg-green-50 h-8"
            data-testid="btn-dashboard-refresh"
            title="Actualizar ahora"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Actualizando…" : "Actualizar"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Wallet} label="Recaudado hoy" value={fmtBs(data.recaudado_hoy)} accent="from-orange-500 to-orange-600" testid="kpi-hoy" hint={fxHint(data.recaudado_hoy, data.contribs_hoy)} />
        <KPI icon={CalendarClock} label="Recaudado ayer" value={fmtBs(data.recaudado_ayer)} accent="from-amber-500 to-yellow-500" testid="kpi-ayer" hint={
          (() => {
            const delta = data.delta_ayer_pct;
            const base  = data.recaudado_anteayer || 0;
            const arrow = delta > 0 ? <ArrowUp className="w-3 h-3" /> : delta < 0 ? <ArrowDown className="w-3 h-3" /> : null;
            const cls   = delta > 0 ? "text-green-700 bg-green-50 border-green-200" : delta < 0 ? "text-rose-700 bg-rose-50 border-rose-200" : "text-slate-600 bg-slate-50 border-slate-200";
            return (
              <div className="text-[10.5px] text-slate-500 leading-snug space-y-0.5" data-testid="kpi-ayer-delta">
                {delta !== undefined && delta !== null && (
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${cls}`}>
                      {arrow}{delta > 0 ? "+" : ""}{delta}%
                    </span>
                    <span className="text-[10px] text-slate-500">vs anteayer</span>
                  </div>
                )}
                <p className="text-[10.5px]">Anteayer: <span className="font-semibold text-slate-800">{fmtBs(base)}</span></p>
                {fxHint(data.recaudado_ayer, data.contribs_ayer)}
              </div>
            );
          })()
        } />
        <KPI icon={CalendarRange} label="Últimos 7 días" value={fmtBs(data.recaudado_7dias)} accent="from-sky-600 to-blue-500" testid="kpi-7dias" hint={
          (() => {
            const delta = data.delta_7dias_pct;
            const base  = data.recaudado_7dias_prev || 0;
            const arrow = delta > 0 ? <ArrowUp className="w-3 h-3" /> : delta < 0 ? <ArrowDown className="w-3 h-3" /> : null;
            const cls   = delta > 0 ? "text-green-700 bg-green-50 border-green-200" : delta < 0 ? "text-rose-700 bg-rose-50 border-rose-200" : "text-slate-600 bg-slate-50 border-slate-200";
            return (
              <div className="text-[10.5px] text-slate-500 leading-snug space-y-0.5" data-testid="kpi-7dias-delta">
                {delta !== undefined && delta !== null && (
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${cls}`}>
                      {arrow}{delta > 0 ? "+" : ""}{delta}%
                    </span>
                    <span className="text-[10px] text-slate-500">vs 7 días anteriores</span>
                  </div>
                )}
                <p className="text-[10.5px]">Semana previa: <span className="font-semibold text-slate-800">{fmtBs(base)}</span></p>
                {fxHint(data.recaudado_7dias, data.contribs_7dias)}
              </div>
            );
          })()
        } />
        <KPI icon={TrendingUp} label="Recaudado mes" value={fmtBs(data.recaudado_mes)} accent="from-green-700 to-green-500" testid="kpi-mes" hint={fxHint(data.recaudado_mes, data.contribs_mes)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          icon={Receipt}
          label="Ticket promedio"
          value={fmtBs(data.ticket_promedio_mes || 0)}
          accent="from-teal-600 to-cyan-500"
          testid="kpi-ticket-promedio"
          hint={(() => {
            const cntMes = data.pagos_validados_mes || 0;
            const cntDia = data.pagos_validados_dia || 0;
            const tpDia  = data.ticket_promedio_dia || 0;
            const serieTicket = (data.serie_7dias_ticket || []).map(d => ({
              label: d.fecha.slice(5),
              ticket: d.ticket_promedio,
            }));
            const hasSerie = serieTicket.some(d => d.ticket > 0);
            const first = hasSerie ? serieTicket.find(d => d.ticket > 0)?.ticket || 0 : 0;
            const last  = hasSerie ? [...serieTicket].reverse().find(d => d.ticket > 0)?.ticket || 0 : 0;
            const trend = first > 0 ? Math.round(((last - first) / first) * 100) : 0;
            const trendColor = trend > 0 ? "text-green-700" : trend < 0 ? "text-red-600" : "text-slate-400";
            const cntMesLine = (
              <p className="text-[10.5px] text-slate-600 mb-0.5" data-testid="kpi-ticket-count">
                <FileCheck2 className="w-3 h-3 inline-block mr-1 -mt-0.5 text-slate-500" />
                <span className="font-semibold text-slate-800">{cntMes}</span>
                <span className="text-slate-500"> {cntMes === 1 ? "pago validado en el mes" : "pagos validados en el mes"}</span>
              </p>
            );
            const diaLine = (
              <p className="text-[10.5px] text-slate-600" data-testid="kpi-ticket-hoy">
                <Wallet className="w-3 h-3 inline-block mr-1 -mt-0.5 text-orange-600" />
                <span className="text-slate-500">Hoy: </span>
                <span className="font-semibold text-slate-800">{fmtBs(tpDia)}</span>
                <span className="text-slate-500"> ({cntDia} {cntDia === 1 ? "pago" : "pagos"})</span>
              </p>
            );
            const sparkline = hasSerie ? (
              <div className="mt-1.5 pt-1.5 border-t border-slate-100" data-testid="kpi-ticket-spark">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[9.5px] uppercase tracking-wider text-slate-400">Tendencia 7 días</span>
                  <span className={`text-[10px] font-semibold ${trendColor}`}>
                    {trend > 0 ? "▲" : trend < 0 ? "▼" : "="} {Math.abs(trend)}%
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={30}>
                  <LineChart data={serieTicket} margin={{ top: 2, right: 2, bottom: 0, left: 2 }}>
                    <Tooltip
                      contentStyle={{ fontSize: 10, padding: "4px 8px", background: "#0f172a", border: "none", borderRadius: 4, color: "#fff" }}
                      labelStyle={{ color: "#cbd5e1", fontSize: 9 }}
                      formatter={(v) => [fmtBs(v), "Ticket"]}
                    />
                    <Line type="monotone" dataKey="ticket" stroke="#0d9488" strokeWidth={1.6} dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-[10px] text-slate-400 mt-1" data-testid="kpi-ticket-spark-empty">Sin pagos en los últimos 7 días</p>
            );
            const usdEurLines = cotizacion && (usdRate || eurRate) ? (
              <>
                <p><span className="font-semibold text-green-800">≈ {fmtUSD(toUSD(data.ticket_promedio_mes || 0))}</span></p>
                <p><span className="font-semibold text-orange-700">≈ {fmtEUR(toEUR(data.ticket_promedio_mes || 0))}</span></p>
              </>
            ) : (
              <p className="text-[10px] text-slate-400" data-testid="kpi-fx-missing">Sin cotización activa</p>
            );
            return (
              <div className="text-[10.5px] text-slate-500 leading-snug space-y-0.5">
                {cntMesLine}
                {diaLine}
                {usdEurLines}
                {sparkline}
              </div>
            );
          })()}
        />
        <KPI icon={Users} label="Contribuyentes" value={data.total_contribuyentes} accent="from-emerald-600 to-emerald-400" testid="kpi-contrib" />
        <KPI icon={AlertCircle} label="Deuda total" value={fmtBs(data.deuda_total)} accent="from-rose-500 to-rose-600" testid="kpi-deuda" hint={fxHint(data.deuda_total, data.contribs_deuda)} />
        <KPI icon={Clock} label="Liquidaciones pendientes" value={data.total_liquidaciones_pendientes} accent="from-amber-500 to-amber-600" testid="kpi-pendientes" />
        <KPI icon={MessageSquare} label="Reclamos abiertos" value={data.reclamos_abiertos} accent="from-blue-600 to-blue-400" testid="kpi-reclamos" />
        <KPI icon={ArrowUpRight} label="Pagos por validar" value={fmtBs(data.monto_pendientes_validar || 0)} accent="from-purple-600 to-purple-400" testid="kpi-validar" hint={
          (() => {
            const cnt = data.pagos_pendientes_validar || 0;
            const bs = data.monto_pendientes_validar || 0;
            const cntLine = (
              <p className="text-[10.5px] text-slate-600 mb-0.5" data-testid="kpi-validar-count">
                <FileCheck2 className="w-3 h-3 inline-block mr-1 -mt-0.5 text-slate-500" />
                <span className="font-semibold text-slate-800">{cnt}</span>
                <span className="text-slate-500"> {cnt === 1 ? "pago pendiente" : "pagos pendientes"}</span>
              </p>
            );
            if (!cotizacion || (!usdRate && !eurRate)) {
              return (
                <div className="leading-snug space-y-0.5">
                  {cntLine}
                  <p className="text-[10px] text-slate-400" data-testid="kpi-fx-missing">Sin cotización activa</p>
                </div>
              );
            }
            return (
              <div className="text-[10.5px] text-slate-500 leading-snug space-y-0.5" data-testid="kpi-fx-hint">
                {cntLine}
                <p><span className="font-semibold text-green-800">≈ {fmtUSD(toUSD(bs))}</span></p>
                <p><span className="font-semibold text-orange-700">≈ {fmtEUR(toEUR(bs))}</span></p>
              </div>
            );
          })()
        } />
        <KPI
          icon={PiggyBank}
          label="Créditos fiscales disponibles"
          value={fmtBs(data.creditos_fiscales_total_bs || 0)}
          accent="from-emerald-500 to-teal-500"
          testid="kpi-creditos-fiscales"
          hint={
            (() => {
              const cnt = data.creditos_fiscales_contribuyentes || 0;
              const bs = data.creditos_fiscales_total_bs || 0;
              const cntLine = (
                <p className="text-[10.5px] text-slate-600 mb-0.5" data-testid="kpi-creditos-count">
                  <Users className="w-3 h-3 inline-block mr-1 -mt-0.5 text-slate-500" />
                  <span className="font-semibold text-slate-800">{cnt}</span>
                  <span className="text-slate-500"> {cnt === 1 ? "contribuyente" : "contribuyentes"}</span>
                </p>
              );
              if (!cotizacion || (!usdRate && !eurRate)) {
                return (
                  <div className="leading-snug space-y-0.5">
                    {cntLine}
                    <p className="text-[10px] text-slate-400">Sin cotización activa</p>
                  </div>
                );
              }
              return (
                <div className="text-[10.5px] text-slate-500 leading-snug space-y-0.5" data-testid="kpi-fx-hint">
                  {cntLine}
                  <p><span className="font-semibold text-green-800">≈ {fmtUSD(toUSD(bs))}</span></p>
                  <p><span className="font-semibold text-orange-700">≈ {fmtEUR(toEUR(bs))}</span></p>
                </div>
              );
            })()
          }
        />
        {data.adopcion_online && (() => {
          const adop = data.adopcion_online.mes_actual || {};
          const delta = data.adopcion_online.delta_pct_cantidad || 0;
          const arrow = delta > 0 ? <ArrowUp className="w-3 h-3" /> : delta < 0 ? <ArrowDown className="w-3 h-3" /> : null;
          const deltaColor = delta > 0 ? "text-green-700 bg-green-50 border-green-200" : delta < 0 ? "text-rose-700 bg-rose-50 border-rose-200" : "text-slate-600 bg-slate-50 border-slate-200";
          const hint = (
            <div className="flex items-center gap-1.5">
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${deltaColor}`}>
                {arrow}{delta > 0 ? "+" : ""}{delta}%
              </span>
              <span className="text-[10px] text-slate-500">vs mes anterior ({adop.online || 0}/{adop.total || 0} pagos)</span>
            </div>
          );
          return (
            <KPI
              icon={Smartphone}
              label="Adopción Portal Online (mes)"
              value={`${adop.pct_cantidad || 0}%`}
              accent="from-teal-600 to-cyan-500"
              testid="kpi-adopcion-online"
              hint={hint}
            />
          );
        })()}
      </div>

      <Card className="p-6 border-green-100 bg-white" data-testid="chart-recaudacion">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-bold text-lg text-green-900">Recaudación últimos 7 días</h3>
            <p className="text-xs text-green-700/60">Pagos validados, en bolívares · excluye anulados</p>
          </div>
          <span className="px-2.5 py-1 rounded-md bg-orange-50 text-orange-700 text-xs font-semibold border border-orange-100 inline-flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full bg-orange-500 ${refreshing ? "animate-ping" : "animate-pulse"}`}></span>
            En vivo
          </span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={serie}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8DD" />
              <XAxis dataKey="label" stroke="#15803D" fontSize={11} />
              <YAxis stroke="#15803D" fontSize={11} />
              <Tooltip formatter={(v) => fmtBs(v)} contentStyle={{ borderRadius: 8, border: "1px solid #16A34A" }} />
              <Line type="monotone" dataKey="monto" stroke="#EA580C" strokeWidth={3} dot={{ r: 4, fill: "#EA580C" }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
